<template>
  <div
    class="flex w-full items-center rounded-md border border-neutral-300 bg-neutral-0 px-4 focus-within:border-secondary-500 focus-within:shadow-[0px_0px_4px_theme('colors.primary.500')] hover:border-secondary-500 active:border-secondary-500 disabled:border-neutral-400 disabled:bg-neutral-50"
    :class="[
      size === 'xs' ? 'py-2' : '',
      size === 'small' ? 'py-2.5' : '',
      size === 'medium' ? 'py-4' : '',
      size === 'large' ? 'py-4' : ''
    ]"
  >
    <div class="mr-3 flex items-center empty:hidden">
      <slot />
    </div>

    <textarea
      :id="labelId || undefined"
      ref="inputElement"
      :value="modelValue"
      class="h-full w-full border-none p-0 leading-normal leading-3 text-neutral-800 outline-none transition-colors placeholder:text-neutral-300 focus:ring-0"
      :class="[
        size === 'xs' || size === 'small' ? 'h-4 text-xs' : '',
        size === 'medium' ? 'text-sm' : '',
        size === 'large' ? 'text-base' : ''
      ]"
      :placeholder="placeholder"
      :name="name"
      :disabled="disabled"
      :required="required"
      :minlength="minlength"
      :maxlength="maxlength"
      :pattern="pattern"
      :rows="rows"
      @input="onInput"
    />
  </div>
</template>

<script lang="ts" setup>
import { inject, ref } from 'vue';
import { FieldLabelIdInjectionKey } from './InfuseFieldInjection';

const emit = defineEmits(['update:modelValue']);

interface Props {
  modelValue?: string | number;
  size?: 'xs' | 'small' | 'medium' | 'large';
  placeholder?: string;
  name?: string;
  disabled?: boolean;
  required?: boolean;
  minlength?: number;
  maxlength?: number;
  rows?: number;
  pattern?: string;
}

withDefaults(defineProps<Props>(), {
  modelValue: '',
  size: 'medium',
  placeholder: '',
  name: '',
  required: false,
  minlength: undefined,
  maxlength: undefined,
  rows: 4,
  pattern: undefined
});

const focus = () => {
  textAreaElement.value?.focus();
};

defineExpose({ focus });

const labelId = inject(FieldLabelIdInjectionKey, null);

const textAreaElement = ref<InstanceType<typeof HTMLTextAreaElement> | null>();

function onInput(event: Event) {
  emit('update:modelValue', (event.target as HTMLTextAreaElement).value);
}
</script>
